import React, { useState } from "react";
import { Box, Alert, CircularProgress, Tabs, Tab, useTheme, useMediaQuery } from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  FitnessCenter as FitnessIcon,
  AttachMoney as PriceIcon,
  LocationOn as LocationIcon,
  AccountBalanceWallet as WalletIcon,
  Payments as PaymentsIcon,
} from "@mui/icons-material";

import { useDateRangeFilter } from "./hooks/useDateRangeFilter";
import { useStatisticsData } from "./hooks/useStatisticsData";

import StatisticsFilters from "./components/StatisticsFilters";
import RevenueStatistics from "./components/RevenueStatistics";
import PopularStatistics from "./components/PopularStatistics";
import CostsStatistics from "./components/CostsStatistics";
import LocationProfitStatistics from "./components/LocationProfitStatistics";
import SafeReconciliation from "./components/SafeReconciliation";
import TrainerPayouts from "./components/TrainerPayouts";

const TABS = [
  { key: "revenue", label: "Przychody", icon: <TrendingUpIcon /> },
  { key: "popular", label: "Popularne", icon: <FitnessIcon /> },
  { key: "costs", label: "Koszty i Zysk", icon: <PriceIcon /> },
  { key: "locations", label: "Zysk po lokalizacji", icon: <LocationIcon /> },
  { key: "safe", label: "Sejf", icon: <WalletIcon /> },
  { key: "payouts", label: "Wypłaty dla trenerów", icon: <PaymentsIcon /> },
];

const Statistics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [tabValue, setTabValue] = useState(0);
  const activeTabKey = TABS[tabValue].key;

  const {
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    selectedPreset,
    handlePresetSelection,
    handleApply,
    handleClearDates,
    appliedDateFrom,
    appliedDateTo,
    formatDate,
  } = useDateRangeFilter();

  const {
    dashboardData,
    costData,
    locationData,
    safeData,
    payoutData,
    loading,
    error,
    forceRefetchActive,
  } = useStatisticsData({ appliedDateFrom, appliedDateTo, activeTabKey });

  const activeData = [dashboardData, dashboardData, costData, locationData, safeData, payoutData][tabValue];
  const showSpinner = loading && !activeData;

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2, md: 3 } }}>
      <StatisticsFilters
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        selectedPreset={selectedPreset}
        loading={loading}
        onRefresh={forceRefetchActive}
        onApply={handleApply}
        onClearDates={handleClearDates}
        onPresetSelection={handlePresetSelection}
        formatDate={formatDate}
      />

      <Tabs
        value={tabValue}
        onChange={(_, newValue) => setTabValue(newValue)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
        variant={isMobile ? "scrollable" : "standard"}
        scrollButtons={isMobile ? "auto" : false}
      >
        {TABS.map((tab) => (
          <Tab key={tab.key} icon={tab.icon} iconPosition="start" label={tab.label} />
        ))}
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showSpinner && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!showSpinner && !error && (
        <>
          {tabValue === 0 && <RevenueStatistics dashboardData={dashboardData} />}
          {tabValue === 1 && <PopularStatistics dashboardData={dashboardData} />}
          {tabValue === 2 && <CostsStatistics costData={costData} />}
          {tabValue === 3 && <LocationProfitStatistics locationData={locationData} />}
          {tabValue === 4 && <SafeReconciliation safeData={safeData} />}
          {tabValue === 5 && (
            <TrainerPayouts payoutData={payoutData} dateFrom={appliedDateFrom} dateTo={appliedDateTo} />
          )}
        </>
      )}
    </Box>
  );
};

export default Statistics;
